import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { COMMAND_PRIORITY_LOW, KEY_TAB_COMMAND, LexicalEditor } from 'lexical';
import React, { useCallback, useEffect } from 'react';

interface TabCyclePluginProps {
    cancelButtonRef: React.RefObject<HTMLButtonElement>;
    saveButtonRef: React.RefObject<HTMLButtonElement>;
}

function handleButtonTabKeyDown(
    editor: LexicalEditor,
    e: KeyboardEvent,
    focusEditorOnShiftKey: boolean,
    otherButtonRef: React.RefObject<HTMLButtonElement>
) {
    if (e.key !== 'Tab') return;
    e.preventDefault();
    if (e.shiftKey === focusEditorOnShiftKey) {
        editor.focus();
    } else if (otherButtonRef.current && !otherButtonRef.current.disabled) {
        otherButtonRef.current.focus();
    } else {
        editor.focus();
    }
}

export function TabCyclePlugin({ cancelButtonRef, saveButtonRef }: TabCyclePluginProps) {
    const [editor] = useLexicalComposerContext();

    const handleCancelKeyDown = useCallback(
        (e: KeyboardEvent) => handleButtonTabKeyDown(editor, e, true, saveButtonRef),
        [editor, saveButtonRef]
    );

    const handleSaveKeyDown = useCallback(
        (e: KeyboardEvent) => handleButtonTabKeyDown(editor, e, false, cancelButtonRef),
        [editor, cancelButtonRef]
    );

    const handleTabCommand = useCallback(
        (event: KeyboardEvent | null) => {
            event?.preventDefault();
            if (event?.shiftKey) {
                saveButtonRef.current?.focus();
            } else {
                cancelButtonRef.current?.focus();
            }
            return true;
        },
        [cancelButtonRef, saveButtonRef]
    );

    useEffect(() => {
        const cancelEl = cancelButtonRef.current;
        const saveEl = saveButtonRef.current;

        const removeTabCommand = editor.registerCommand(
            KEY_TAB_COMMAND,
            handleTabCommand,
            COMMAND_PRIORITY_LOW
        );

        cancelEl?.addEventListener('keydown', handleCancelKeyDown);
        saveEl?.addEventListener('keydown', handleSaveKeyDown);

        return () => {
            removeTabCommand();
            cancelEl?.removeEventListener('keydown', handleCancelKeyDown);
            saveEl?.removeEventListener('keydown', handleSaveKeyDown);
        };
    }, [
        editor,
        cancelButtonRef,
        saveButtonRef,
        handleCancelKeyDown,
        handleSaveKeyDown,
        handleTabCommand,
    ]);

    return null;
}
